/*!
 * Copyright © 2005-2025 Hyland Software, Inc. and its affiliates. All rights reserved.
 *
 * Alfresco Example Content Application
 *
 * This file is part of the Alfresco Example Content Application.
 * If the software was purchased under a paid Alfresco license, the terms of
 * the paid license agreement will prevail. Otherwise, the software is
 * provided under the following open source license terms:
 *
 * The Alfresco Example Content Application is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Lesser General Public License as published by
 * the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * The Alfresco Example Content Application is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Lesser General Public License for more details.
 *
 * You should have received a copy of the GNU Lesser General Public License
 * from Hyland Software. If not, see <http://www.gnu.org/licenses/>.
 */

import { LoginComponent, LoginFooterDirective, AppConfigService, UserPreferencesService } from '@alfresco/adf-core';
import { Component, inject, ViewEncapsulation } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { TranslatePipe } from '@ngx-translate/core';
import { AppSettingsService } from '@alfresco/aca-shared';
import { BrandingService } from '../branding/branding.service';
import { AppLanguageService } from '../shared/language.service';
import { DisplayPreferencesService } from '../shared/display-preferences.service';
import { Event, NavigationCancel, NavigationEnd, NavigationError, Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { filter, take } from 'rxjs/operators';

interface LanguageOption {
  key: string;
  label: string;
}

/**
 * Sign-in screen.
 *
 * The form itself is ADF's `<adf-login>`, so authentication, validation and the
 * error messages stay the product's own; everything around it is this screen's
 * chrome. Two controls the design calls for are not offered as working buttons,
 * because this deployment cannot honour them:
 *
 * - **Single sign-on.** `app.config.json` sets `authType: "BASIC"`, so there is
 *   no identity provider to send anyone to. The buttons appear only when
 *   `authType` is "OAUTH"; a disabled button with a configuration note was
 *   noise on the one screen everybody sees.
 * - **Password reset.** Alfresco Content Services has no self-service reset, so
 *   the link explains who to ask instead of leading nowhere.
 */
@Component({
  imports: [CommonModule, FormsModule, MatIconModule, LoginComponent, LoginFooterDirective, TranslatePipe],
  templateUrl: './app-login.component.html',
  styleUrls: ['./app-login.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class AppLoginComponent {
  /** Nobody is signed in here, so this is the brand the shell last read and
   *  left in browser storage; a fresh browser shows the configured one. */
  readonly branding = inject(BrandingService);

  private readonly config = inject(AppConfigService);
  private readonly languageService = inject(AppLanguageService);
  private readonly userPreferences = inject(UserPreferencesService);
  private readonly displayPrefs = inject(DisplayPreferencesService);
  private readonly router = inject(Router);

  readonly settings = inject(AppSettingsService);

  /** Languages from `app.config.json`, the same list the settings screen uses. */
  readonly languages: LanguageOption[] = this.config.get<LanguageOption[]>('languages', []) ?? [];

  language = this.userPreferences.locale ?? '';

  /** Whether an identity provider is configured to sign in against. */
  readonly ssoConfigured = String(this.config.get<string>('authType', 'BASIC')).toUpperCase() === 'OAUTH';

  /** Buddhist Era year, to match every other date in the product. */
  readonly year = new Date().getFullYear() + 543;

  showResetHelp = false;

  /**
   * Switches the interface language.
   *
   * The guest key is written as well as the user's, because nobody is signed in
   * yet: ADF reads preferences under `GUEST` until a session exists, and the
   * choice should still hold once one does.
   */
  changeLanguage(key: string): void {
    this.language = key;
    this.languageService.use(key);
  }

  /**
   * Sends the person to the landing page they chose in Settings.
   *
   * ADF navigates as soon as it emits `success` — to the page a guard sent
   * them away from, or else to `successRoute`. Only the second is replaced:
   * someone who followed a link to a document still lands on that document.
   */
  onSignedIn(): void {
    const settled = this.router.events.pipe(
      filter((e: Event) => e instanceof NavigationEnd || e instanceof NavigationCancel || e instanceof NavigationError),
      take(1)
    );
    forkJoin({ prefs: this.displayPrefs.load(), nav: settled }).subscribe(({ prefs, nav }) => {
      if (nav instanceof NavigationEnd && nav.urlAfterRedirects === '/dashboard' && prefs.landing !== DisplayPreferencesService.DEFAULT_LANDING) {
        void this.router.navigate(['/' + prefs.landing], { replaceUrl: true });
      }
    });
  }
}
